import { fetchAppLevelScopesTE, fetchUserLevelScopesTE } from 'api/users/scopes';
import { either } from 'fp-ts';
import { useRouter } from 'next/router';
import { allPass } from 'rambda';
import { pipe, errorToReactLeft, ReactLeftError } from 'utils/fp';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { useCurrentUserId, useIsLoggedIn } from 'modules/Auth/hooks';

export type ScopeMatcher = (res: CF.API.Users.FetchScopesResponse) => boolean;
type UserScopesE = either.Either<ReactLeftError<CF.API.ApiError>, CF.API.Users.FetchScopesResponse>;

export function checkIfHasAllScopes(scopeMatchers: ScopeMatcher[], userScopesE: UserScopesE): boolean {
  if (!scopeMatchers.length) {
    return false;
  }

  const checkAllScopes = allPass(scopeMatchers);

  return pipe(
    userScopesE,
    either.map(checkAllScopes),
    either.getOrElse(() => {
      return false;
    }),
  );
}

/**
 * Use this hook only when explicitly requiring the list of app level scopes for viewing user.
 * eg. when checking loading status.
 *
 * @param userAndAppIds - Ids of the app being viewed.
 */
export function useAppLevelScopes(userAndAppIds?: { appId: string; userOrOrgId: string }) {
  const isLoggedIn = useIsLoggedIn();
  const routerQuery = (useRouter()?.query || {}) as { appId: string; userOrOrgId: string };
  const { appId, userOrOrgId } = userAndAppIds ?? routerQuery;

  return useQueryTE(
    [
      queryKeys.AppLevelScopes,
      {
        appId,
        userOrOrgId,
        isLoggedIn,
      },
    ],
    fetchAppLevelScopesTE(
      {
        appId,
        userId: userOrOrgId,
      },
      errorToReactLeft,
    ),
    { enabled: Boolean(isLoggedIn && appId && userOrOrgId) },
  );
}

/**
 * Helper hook to check if all the scopes are present in the app level scopes of the user.
 *
 * @param userAndAppIds - Ids of the app being viewed.
 */
export function useHasAppLevelScopes(scopeMatchers: ScopeMatcher[], userAndAppIds?: { appId: string; userOrOrgId: string }): boolean {
  const { data: userScopesE } = useAppLevelScopes(userAndAppIds);
  return checkIfHasAllScopes(scopeMatchers, userScopesE);
}

/**
 * Use this hook only when explicitly requiring the list of user level scopes for viewing user.
 * eg. when checking loading status.
 */
export function useUserLevelScopes(userId?: string) {
  const isLoggedIn = useIsLoggedIn();
  const currentUserId = useCurrentUserId();
  const fetchForUserId = userId ?? currentUserId;

  return useQueryTE(
    [
      queryKeys.MyScopes,
      {
        userId,
      },
    ],
    fetchUserLevelScopesTE(
      {
        userId: fetchForUserId,
      },
      errorToReactLeft,
    ),
    { enabled: Boolean(isLoggedIn && fetchForUserId) },
  );
}

/**
 * Helper hook to check if all the scopes are present in the user level scopes of the user.
 */
export function useHasUserLevelScopes(scopeMatchers: ScopeMatcher[], userId?: string): boolean {
  const { data: userScopesE } = useUserLevelScopes(userId);
  return checkIfHasAllScopes(scopeMatchers, userScopesE);
}
